import React, { useEffect, useState, useRef } from 'react';
import { useThemeCompat } from '../contexts/ThemeContext';
import { createPortal } from 'react-dom';

interface FighterRadialChartProps {
  percentage: number;
  color?: string;
  position?: 'left' | 'right' | 'center';
  size?: number;
  strokeWidth?: number;
}

const FighterRadialChart: React.FC<FighterRadialChartProps> = ({ 
  percentage, 
  color, 
  position = 'center',
  size = 120,
  strokeWidth = 10
}) => {
  const { theme } = useThemeCompat();
  
  // Default color based on theme if none provided - stronger blue for better visibility on mobile
  const defaultColor = theme === 'light' ? '#0066ff' : '#4d94ff';
  
  // State for the animated percentage value
  const [animatedPercentage, setAnimatedPercentage] = useState(0);
  // State for showing tooltip
  const [showTooltip, setShowTooltip] = useState(false);
  // State for tooltip position
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  // References for animation frame
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  
  // Use theme-aware standardized blue color if none provided
  const chartColor = color || defaultColor;
  
  // Derived values for SVG calculation
  const halfSize = size / 2;
  const radius = halfSize - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;
  
  // Calculate stroke dash offset based on percentage
  const strokeDashoffset = ((100 - percentage) / 100) * circumference;
  
  // Background color with opacity
  const bgColorWithOpacity = theme === 'light' ? 'rgba(200, 200, 200, 0.3)' : 'rgba(70, 70, 70, 0.3)';
  
  // Position classes for tooltip
  let tooltipPositionClasses = 'left-full ml-2';
  let caretPositionClasses = '-left-[6px]';
  
  if (position === 'left') {
    tooltipPositionClasses = 'right-full mr-2';
    caretPositionClasses = '-right-[6px]';
  }
  
  // Increase the stroke width slightly for small screens
  const effectiveStrokeWidth = window.innerWidth < 768 ? Math.max(strokeWidth, 10) : strokeWidth;
  
  // Check if we're on a mobile/touch device
  const isTouchDevice = () => {
    return window.matchMedia('(hover: none), (pointer: coarse)').matches;
  };
  
  // Completely disable tooltips on mobile devices
  useEffect(() => {
    // Only disable tooltips on touch devices
    if (isTouchDevice()) {
      setShowTooltip(false);
    }
  }, []);
  
  // Animation function
  const animate = (timestamp: number) => {
    if (startTimeRef.current === null) {
      startTimeRef.current = timestamp;
    }
    
    const elapsed = timestamp - startTimeRef.current;
    // Animation duration - slightly longer for a more dramatic effect
    const duration = 800;
    
    // Calculate progress with cubic easing for nicer motion
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = progress < 0.5
      ? 4 * progress * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 3) / 2;
    
    // Update state with the current animation value
    setAnimatedPercentage(easedProgress * percentage);
    
    // Continue animation if not complete
    if (progress < 1) {
      animationRef.current = requestAnimationFrame(animate);
    } else {
      // Ensure we end exactly at the target percentage
      setAnimatedPercentage(percentage);
      animationRef.current = null;
      startTimeRef.current = null;
    }
  };
  
  // Start animation when component mounts or percentage changes
  useEffect(() => {
    // Clean up previous animation if it exists
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    // Start a new animation
    startTimeRef.current = null;
    animationRef.current = requestAnimationFrame(animate);
    
    // Cleanup on unmount
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [percentage]);
  
  // Update tooltip position when showing
  useEffect(() => {
    if (showTooltip && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const offset = position === 'left' ? -200 : 20; // Adjust based on position
      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.left + (position === 'left' ? -offset : rect.width + offset)
      });
    }
  }, [showTooltip, position]);

  // Get the gradient colors based on the chart color
  const getGradientColors = (baseColor: string) => {
    // Helper function to adjust color opacity
    const adjustOpacity = (color: string, opacity: number) => {
      // If it's a hex color, convert to RGB
      if (color.startsWith('#')) {
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
      }
      // If it's already RGB/RGBA, just adjust opacity
      return color.replace(/[\d.]+\)$/g, `${opacity})`);
    };

    return {
      start: adjustOpacity(baseColor, 1),    // Full opacity at start
      middle: adjustOpacity(baseColor, 0.8), // 80% opacity in middle
      end: adjustOpacity(baseColor, 0.6)     // 60% opacity at end
    };
  };

  const gradientColors = getGradientColors(chartColor);
  const gradientId = `radialGradient-${position}`;
  
  return (
    <div 
      className="relative inline-flex items-center justify-center"
      ref={containerRef}
    >
      <svg 
        width={size} 
        height={size} 
        viewBox={`0 0 ${size} ${size}`}
        className="transform scale-x-[-1]"
      >
        {/* Define the gradient */}
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={gradientColors.start} />
            <stop offset="50%" stopColor={gradientColors.middle} />
            <stop offset="100%" stopColor={gradientColors.end} />
          </linearGradient>
        </defs>

        {/* Background circle */}
        <circle
          cx={halfSize}
          cy={halfSize}
          r={radius}
          stroke={bgColorWithOpacity}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset="0"
          style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}
        />
        
        {/* Progress circle with animation */}
        <circle
          cx={halfSize}
          cy={halfSize}
          r={radius}
          stroke={`url(#${gradientId})`}
          strokeWidth={effectiveStrokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - (animatedPercentage / 100) * circumference}
          strokeLinecap="round"
          style={{ 
            transform: 'rotate(-90deg)', 
            transformOrigin: 'center',
            transition: 'stroke-width 300ms ease'
          }}
        />
      </svg>
      
      {/* Win percentage in the center without any labels */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl md:text-3xl font-bold text-text-primary dark:text-dark-text-primary">
          {Math.round(animatedPercentage)}%
        </span>
      </div>
    </div>
  );
};

export default FighterRadialChart; 